import React, { useEffect, useRef, useState } from "react";

// Accept portable lengths, including unitless pixel values, without allowing
// arbitrary host CSS to escape the component's presentation contract.
export function formDimension(value: string | null, fallback: string): string {
  const length = value?.trim() || "";
  if (/^\d+(?:\.\d+)?$/.test(length)) return `${length}px`;
  return /^(?:auto|\d+(?:\.\d+)?(?:px|%|rem|em|vw|vh|dvw|dvh|vmin|vmax))$/.test(
    length,
  )
    ? length
    : fallback;
}

export function FormPresentation({
  modal,
  open,
  title,
  triggerLabel,
  width,
  height,
  maxWidth,
  maxHeight,
  onOpen,
  onClose,
  children,
  configuration,
  closeLabel = "Close form",
}: {
  modal: boolean;
  open: boolean;
  title: string;
  triggerLabel: string;
  width: string;
  height: string;
  maxWidth: string;
  maxHeight: string;
  onOpen: () => void;
  onClose: () => void;
  children?: React.ReactNode;
  configuration?: React.ReactNode;
  closeLabel?: string;
}) {
  const [configuring, setConfiguring] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [modal, open]);
  const style = {
    width,
    height,
    maxWidth: `min(${maxWidth === "auto" ? "100%" : maxWidth}, ${modal ? "calc(100vw - 32px)" : "100%"})`,
    maxHeight: modal
      ? `min(${maxHeight === "auto" ? "90dvh" : maxHeight}, calc(100dvh - 32px))`
      : maxHeight === "auto"
        ? "none"
        : maxHeight,
  };
  if (!modal)
    return React.createElement(
      "section",
      { className: "embedded-form", style },
      children,
    );
  return React.createElement(
    React.Fragment,
    null,
    React.createElement(
      "button",
      { type: "button", onClick: onOpen, "aria-haspopup": "dialog" },
      triggerLabel,
    ),
    React.createElement(
      "dialog",
      {
        ref: dialog,
        className: "embedded-dialog",
        "aria-label": title,
        style,
        onCancel: (event: React.SyntheticEvent<HTMLDialogElement>) => {
          // The host owns open state. Native close events can arrive after a newer open request.
          event.preventDefault();
          onClose();
        },
        onClick: (event: React.MouseEvent<HTMLDialogElement>) => {
          if (event.target !== event.currentTarget) return;
          const rect = event.currentTarget.getBoundingClientRect();
          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          )
            onClose();
        },
      },
      React.createElement(
        "header",
        { className: "embedded-dialog-header" },
        React.createElement("h2", null, title),
        configuration
          ? React.createElement(
              "button",
              {
                type: "button",
                "aria-expanded": configuring,
                  "aria-label": configuring ? "Hide configuration" : "Configure popout",
                onClick: () => setConfiguring((value) => !value),
              },
              configuring ? "⚙ Hide configuration" : "⚙ Configure popout",
            )
          : null,
        React.createElement(
          "button",
          { type: "button", onClick: onClose, "aria-label": closeLabel },
          "Close",
        ),
      ),
      React.createElement(
        "div",
        { className: "embedded-dialog-body" },
        configuration
          ? React.createElement("div", { hidden: !configuring }, configuration)
          : null,
        children,
      ),
    ),
  );
}

export const formPresentationCss = `
.embedded-form{max-width:100%;overflow:auto;min-width:0}
.embedded-dialog{box-sizing:border-box;padding:0;border:1px solid #d7dfda;border-radius:12px;color:inherit;background:#fff;overflow:auto}
.embedded-dialog::backdrop{background:rgb(16 35 35 / 55%)}
.embedded-dialog-header{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 20px;border-bottom:1px solid #d7dfda}
.embedded-dialog-header h2{font-size:18px;margin:0;overflow-wrap:anywhere}
.embedded-dialog-body{padding:20px;min-width:0}
.embedded-dialog .embedded-dialog-body .canvas-shell{max-width:100%;overflow:auto}
`;
